/**
 * Catalog loading + cache (#42, 2026-06-12).
 *
 * Kept OUT of `provider.tsx` so that file only exports a component (satisfies
 * react-refresh/only-export-components without a disable). `en` is eager and
 * always cached; every other locale is a dynamic `import()` so Rollup emits it
 * as its own async chunk and the eager bundle never carries non-default
 * locales. Loaded catalogs are memoised here so re-selecting is instant.
 */

import { EN } from './catalog-en';
import type { Catalog } from './catalog-en';
import type { Locale } from './context';

/** Catalogs already in memory. `en` is always present (eager import). */
export const CATALOGS = new Map<Locale, Catalog>([['en', EN]]);

/**
 * Lazy-load a locale's catalog. `en` resolves synchronously (already cached);
 * every other locale is a dynamic `import()` so it lands in its own async
 * chunk. Exported so tests can assert the lazy path resolves the right keys.
 */
export async function loadCatalog(locale: Locale): Promise<Catalog> {
  const cached = CATALOGS.get(locale);
  if (cached) return cached;
  let mod: { default: Catalog };
  switch (locale) {
    case 'af':
      mod = await import('./catalog-af');
      break;
    default:
      // Unknown locale — fall back to the eager base rather than throwing.
      return EN;
  }
  CATALOGS.set(locale, mod.default);
  return mod.default;
}
